import { type ReactNode } from "react";

/**
 * Pills for the navy/blue auth/admin screens (controlplane-auth task 11.2, echoing AgentDNA's
 * `SeverityPill`). A pill is a small, rounded status/label chip. Like the existing `StatusBadge`
 * (design 12.4), it conveys state by TEXT first — the colour from the `.oa-auth` status tokens is a
 * redundant channel, never the only one — so it reads for colour-blind and screen-reader users.
 *
 * Two flavours cover the admin surface:
 *   - `SeverityPill`  — a severity level (info / safe / warn / threat), mapped to the theme's status
 *                       tokens (`--info`, `--safe`, `--warn`, `--threat`).
 *   - `StatusPill`    — a neutral labelled chip in a small tone set (used for member status, roles,
 *                       enabled/disabled, etc.).
 *
 * They are presentational: a caller passes a resolved level/tone and the visible text; the pill
 * renders a `<span>` with a `data-*` hook the scoped CSS styles. The accessible name is the visible
 * text (plus an optional longer `title`).
 */

/** Severity levels, ordered low→high; mapped to the `.oa-auth` status colour tokens. */
export type Severity = "info" | "safe" | "warn" | "threat";

/** The visible default label per severity; a caller may override. */
const SEVERITY_LABEL: Readonly<Record<Severity, string>> = {
  info: "Info",
  safe: "OK",
  warn: "Warning",
  threat: "Critical",
};

export interface SeverityPillProps {
  /** The severity; drives the colour token (redundant to the text). */
  readonly severity: Severity;
  /** Visible text; defaults to a per-severity word. */
  readonly label?: string;
  /** A longer accessible name / tooltip; defaults to the visible label. */
  readonly title?: string;
}

export function SeverityPill(props: SeverityPillProps): ReactNode {
  const label = props.label ?? SEVERITY_LABEL[props.severity];
  const title = props.title ?? label;
  return (
    <span className="oa-pill oa-pill--severity" data-severity={props.severity} title={title}>
      <span className="oa-pill__dot" aria-hidden="true" />
      <span className="oa-pill__label">{label}</span>
    </span>
  );
}

/** Neutral tones for labelled chips (role, membership status, flags). */
export type PillTone = "neutral" | "accent" | "safe" | "warn" | "threat" | "muted";

export interface StatusPillProps {
  /** The chip's visible text — the primary (non-colour) carrier of meaning. */
  readonly children: ReactNode;
  /** The tone; a redundant colour channel. Defaults to `neutral`. */
  readonly tone?: PillTone;
  /** A longer accessible name / tooltip. */
  readonly title?: string;
}

export function StatusPill(props: StatusPillProps): ReactNode {
  return (
    <span
      className="oa-pill oa-pill--status"
      data-tone={props.tone ?? "neutral"}
      {...(props.title === undefined ? {} : { title: props.title })}
    >
      <span className="oa-pill__label">{props.children}</span>
    </span>
  );
}
